import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Loader2, AlertTriangle, Printer, ClipboardList, Calendar, Heart, FileDown,
} from 'lucide-react';
import { API_URL as API } from '../../utils/api';
import { useTitreOnglet } from '../../hooks/useTitreOnglet';

export default function BilanFichePatientView() {
  useTitreOnglet('Fiche bilan');
  const { token } = useParams();
  const [state, setState] = useState('loading'); // loading / ok / missing / expired
  const [doc, setDoc] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const ficheRef = useRef(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/bilan/fiche/${encodeURIComponent(token)}`);
        if (!alive) return;
        setDoc(data);
        setState('ok');
      } catch (e) {
        if (!alive) return;
        if (e.response?.status === 410) setState('expired');
        else setState('missing');
      }
    })();
    return () => { alive = false; };
  }, [token]);

  if (state === 'loading') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-violet-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
      </div>
    );
  }

  if (state === 'missing' || state === 'expired') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-violet-50 flex items-center justify-center px-4">
        <div className="max-w-md text-center">
          <div className="inline-block bg-rose-100 rounded-3xl p-5 mb-4"><AlertTriangle className="w-9 h-9 text-rose-500" /></div>
          <h2 className="text-xl font-bold text-slate-800 mb-2">
            {state === 'expired' ? 'Fiche expirée' : 'Fiche introuvable'}
          </h2>
          <p className="text-sm text-slate-500">
            {state === 'expired'
              ? "Cette fiche bilan n'est plus disponible. Les fiches sont accessibles pendant 7 jours après leur envoi."
              : "Ce lien ne correspond à aucune fiche. Vérifiez qu'il est complet."}
          </p>
        </div>
      </div>
    );
  }

  const fiche = doc?.fiche || {};
  const doctorName = doc?.doctor_name || 'votre médecin';
  const expiresAt = doc?.expires_at ? new Date(doc.expires_at) : null;

  const handleDownloadPdf = async () => {
    if (!ficheRef.current || downloading) return;
    setDownloading(true);
    try {
      const html2pdf = (await import('html2pdf.js')).default;
      const safeCode = (fiche.code || 'bilan').toString().replace(/[^a-z0-9-]/gi, '_');
      await html2pdf()
        .from(ficheRef.current)
        .set({
          margin: [10, 10, 10, 10],
          filename: `Fiche-Bilan-${safeCode}.pdf`,
          image: { type: 'jpeg', quality: 0.95 },
          html2canvas: { scale: 2, useCORS: true, backgroundColor: '#ffffff' },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
          pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
        })
        .save();
    } catch (e) {
      // Fallback: trigger native print dialog (user can choose "Save as PDF")
      window.print();
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-violet-50 print:bg-white print:min-h-0" data-testid="bilan-fiche-view">
      {/* Print styles keep the page clean when printed */}
      <style>{`
        @media print {
          .print\\:hide { display: none !important; }
          body { background: white !important; }
          .print\\:shadow-none { box-shadow: none !important; }
          .print\\:border-0 { border: 0 !important; }
        }
      `}</style>

      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Header with action bar */}
        <div className="flex items-center justify-between mb-6 print:hide flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <div className="bg-gradient-to-br from-indigo-500 to-violet-500 rounded-xl p-2 shadow-md">
              <ClipboardList className="w-5 h-5 text-white" />
            </div>
            <span className="font-semibold text-slate-700">Fiche bilan</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleDownloadPdf}
              disabled={downloading}
              data-testid="download-pdf-btn"
              className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-indigo-200 text-indigo-700 rounded-full text-sm font-medium shadow-sm hover:bg-indigo-50 hover:border-indigo-300 transition-all disabled:opacity-60"
            >
              {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
              {downloading ? 'Génération…' : 'Télécharger PDF'}
            </button>
            <button
              onClick={() => window.print()}
              data-testid="print-btn"
              className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-500 to-violet-500 text-white rounded-full text-sm font-medium shadow-lg hover:shadow-xl transition-all hover:scale-105"
            >
              <Printer className="w-4 h-4" /> Imprimer
            </button>
          </div>
        </div>

        {/* Title card */}
        <div ref={ficheRef} className="bg-white rounded-3xl p-8 shadow-xl mb-6 print:shadow-none print:border-0 print:rounded-none print:p-0">
          <div className="text-center mb-6 print:mb-4">
            <h1 className="text-3xl font-bold text-slate-800 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Fiche Bilan personnalisée
            </h1>
            <p className="text-sm text-slate-500">De la part de {doctorName}</p>
          </div>

          {/* Info banner (hidden on print) */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 print:hide flex items-start gap-3">
            <Printer className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 text-sm">
              <p className="font-semibold text-amber-900 mb-0.5">Pensez à imprimer cette fiche</p>
              <p className="text-amber-700 text-xs">Pour la conserver avec votre dossier médical. Cette page reste disponible pendant 7 jours.</p>
            </div>
          </div>

          {/* Sections */}
          <Section title="Synthèse clinique" icon="🎯" color="indigo">
            <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{fiche.synthese_clinique}</p>
          </Section>

          {fiche.points_attention?.length > 0 && (
            <Section title="Points d'attention" icon="⚠️" color="amber">
              <ul className="space-y-1.5">
                {fiche.points_attention.map((p, i) => (
                  <li key={`pa-${i}-${p?.slice(0, 24) || ''}`} className="text-sm text-slate-700 leading-relaxed flex items-start gap-2">
                    <span className="text-amber-500 flex-shrink-0">•</span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {fiche.recommandations?.length > 0 && (
            <Section title="Recommandations personnalisées" icon="💊" color="emerald">
              <ul className="space-y-1.5">
                {fiche.recommandations.map((p, i) => (
                  <li key={`reco-${i}-${p?.slice(0, 24) || ''}`} className="text-sm text-slate-700 leading-relaxed flex items-start gap-2">
                    <span className="text-emerald-500 flex-shrink-0">•</span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {fiche.prevention?.length > 0 && (
            <Section title="Prévention" icon="🛡" color="sky">
              <ul className="space-y-1.5">
                {fiche.prevention.map((p, i) => (
                  <li key={`prev-${i}-${p?.slice(0, 24) || ''}`} className="text-sm text-slate-700 leading-relaxed flex items-start gap-2">
                    <span className="text-sky-500 flex-shrink-0">•</span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {/* Footer */}
          <div className="mt-8 pt-5 border-t border-slate-100 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-1.5 text-[11px] text-slate-400">
              <Heart className="w-3 h-3 text-rose-400" />
              <span>Document préparé par votre médecin</span>
            </div>
            {expiresAt && (
              <div className="flex items-center gap-1 text-[11px] text-slate-400">
                <Calendar className="w-3 h-3" />
                <span>Disponible jusqu'au {expiresAt.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })}</span>
              </div>
            )}
          </div>
        </div>

        <p className="text-center text-[11px] text-slate-400 italic print:hide">
          Cette fiche ne remplace pas une consultation médicale. Pour toute question, contactez votre médecin.
        </p>
      </div>
    </div>
  );
}

function Section({ title, icon, color, children }) {
  const colorMap = {
    indigo: 'border-indigo-200 bg-indigo-50/40',
    amber: 'border-amber-200 bg-amber-50/40',
    emerald: 'border-emerald-200 bg-emerald-50/40',
    sky: 'border-sky-200 bg-sky-50/40',
  };
  return (
    <div className={`rounded-2xl border-2 ${colorMap[color] || ''} p-4 mb-4 print:border print:rounded-lg print:p-3 print:mb-3`}>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xl">{icon}</span>
        <h3 className="font-bold text-slate-800 text-base" style={{ fontFamily: 'Outfit, sans-serif' }}>{title}</h3>
      </div>
      {children}
    </div>
  );
}
